import { createSelector } from '@reduxjs/toolkit';

import {
  Answer,
  PossibleQuestionModelType,
  Question,
} from '@/domain/impact/impact.entity.ts';
import {
  EnvQuestion,
  EnvQuestions,
} from '@/domain/impact-env/entities/question.entity.ts';
import {
  selectImpactEnvAnswersMap,
  selectImpactEnvQuestions,
} from '@/domain/impact-env/impact-env.selectors.ts';
import {
  EssQuestion,
  EssQuestions,
} from '@/domain/impact-ess/entities/question.entity.ts';
import {
  selectImpactEssAnswersMap,
  selectImpactEssQuestions,
} from '@/domain/impact-ess/impact-ess.selectors.ts';

export const selectImpactEssQuestionsMap = createSelector(
  [selectImpactEssQuestions, selectImpactEssAnswersMap],
  (
    impactEnvQuestions,
    impactEnvAnswersMap,
  ): Map<
    Question<PossibleQuestionModelType.ESS>['paramId'],
    Question<PossibleQuestionModelType.ESS>
  > => {
    return formatQuestionsMap<PossibleQuestionModelType.ESS>(
      impactEnvQuestions,
      impactEnvAnswersMap,
    );
  },
);

export const selectImpactEnvQuestionsMap = createSelector(
  [selectImpactEnvQuestions, selectImpactEnvAnswersMap],
  (
    impactEnvQuestions,
    impactEnvAnswersMap,
  ): Map<
    Question<PossibleQuestionModelType.ENV>['paramId'],
    Question<PossibleQuestionModelType.ENV>
  > => {
    return formatQuestionsMap<PossibleQuestionModelType.ENV>(
      impactEnvQuestions,
      impactEnvAnswersMap,
    );
  },
);

export function formatQuestionsMap<T extends PossibleQuestionModelType>(
  questions: EnvQuestions | EssQuestions,
  answersMap: Map<string, Answer>,
) {
  const impactEnvQuestionsMap: Map<
    Question<T>['paramId'],
    Question<T>
  > = new Map();
  for (const question of Object.values(questions)) {
    const isHidden = checkIfQuestionIsHidden(question, answersMap);
    impactEnvQuestionsMap.set(question.paramId, {
      paramId: question.paramId,
      topic: question.topic,
      section: question.section,
      type: question.type,
      answer: answersMap.get(question.paramId),
      isHidden: isHidden,
      title: question.title,
      options: question.options,
      fields: question.fields,
      helpText: question.helpText,
    });
  }
  return impactEnvQuestionsMap;
}

type DependencyRule = {
  conditions: {
    $eq: boolean | string | number;
  };
  value: boolean;
};

function checkIfQuestionIsHidden(
  question: EnvQuestion | EssQuestion,
  answersMap: Map<string, Answer>,
) {
  if (
    !question.dependency?.questions ||
    question.dependency.questions.length <= 0
  ) {
    return false;
  }

  // We're taking the first dependency question for the moment
  // Multiple questions dependencies will come later
  const firstDependencyQuestionId = question.dependency.questions[0];
  const dependencyAnswer = answersMap.get(firstDependencyQuestionId);

  if (!question?.dependency?.rules || !dependencyAnswer?.value) {
    return false;
  }

  const dependencyRules: DependencyRule[] = question.dependency.rules;

  // look in in dependancy.rules array if one condition is valid
  const validDepencencyRule = dependencyRules.find((rule: DependencyRule) => {
    // This handle only condition $eq
    // `eq can compare boolean, string, (number ?)
    return rule.conditions.$eq.toString() === dependencyAnswer.value.toString();
  });

  if (!validDepencencyRule) {
    return false;
  }

  const isHidden = validDepencencyRule.value;
  return isHidden;
}
